"use client";
import { useState } from "react";
import Heart from "lucide-react/dist/esm/icons/heart";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { readEdgeFunctionError } from "@/lib/edge-function-error";
import { effectiveSellerSharePercent, formatFeePercent } from "@/lib/platform-fees";

const PRESET_AMOUNTS = [1, 3, 5, 10];

interface TipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  assetTitle: string;
  sellerName: string;
}

export function TipDialog({ open, onOpenChange, assetId, assetTitle, sellerName }: TipDialogProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const [custom, setCustom] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const amount = selected !== null ? selected : (parseFloat(custom) || 0);

  const handleTip = async () => {
    if (amount <= 0) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-tip", {
        body: { asset_id: assetId, amount },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("Tip checkout did not start. Please try again.");
      window.location.href = data.url;
    } catch (err) {
      // create-tip says why ("can't receive tips yet", "your own listing"…) —
      // the generic non-2xx message hides that.
      const failure = await readEdgeFunctionError(err);
      toast.error(failure.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[360px] p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-rose-500/10 to-pink-500/5 p-5 pb-4 border-b border-border/50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
              Tip the Creator
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Support <span className="font-medium text-foreground">{sellerName}</span> for making{" "}
              <span className="font-medium text-foreground">{assetTitle}</span>
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {PRESET_AMOUNTS.map((amt) => (
              <button
                key={amt}
                type="button"
                onClick={() => { setSelected(amt); setCustom(""); }}
                className={cn(
                  "rounded-lg border-2 py-2 text-sm font-medium transition-colors",
                  selected === amt
                    ? "border-rose-500 bg-rose-500/10 text-rose-600 dark:text-rose-400"
                    : "border-border hover:border-rose-500/50"
                )}
              >
                ${amt}
              </button>
            ))}
          </div>

          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="Custom amount (USD)"
              value={custom}
              onChange={(e) => { setCustom(e.target.value); setSelected(null); }}
              aria-label="Custom tip amount"
              className="pl-7"
            />
          </div>

          {amount > 0 && (
            <p className="text-xs text-muted-foreground text-center">
              ${amount.toFixed(2)} tip · {formatFeePercent(effectiveSellerSharePercent("seller_free"))} goes directly to {sellerName}
            </p>
          )}

          <Button
            className="w-full"
            disabled={amount <= 0 || isLoading}
            onClick={handleTip}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Heart className="h-4 w-4 mr-2 fill-current" />
            )}
            {isLoading ? "Processing…" : `Send $${amount > 0 ? amount.toFixed(2) : "0.00"} Tip`}
          </Button>

          <p className="text-label text-muted-foreground text-center">
            Tips are processed securely and are non-refundable
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
